import cx from 'classnames'
import { type ReactNode, useEffect, useState } from 'react'

import { type SanityImageFragment } from '@data/sanity/queries/types/image'
import { type SanityFooterSettings } from '@data/sanity/queries/types/site'

import Newsletter from '@blocks/newsletter'
import Menu from '@blocks/navigation/menu'
import Icon, { IconName } from '@components/icon'
import LanguageSwitch from '@components/language-switch'
import Photo from '@components/photo'

interface FooterBlockProps {
  title?: string
  children?: ReactNode
  className?: string
}

interface FooterLinkProps {
  id: string
  children: ReactNode
  href?: string
  icon?: IconName
}

type FooterProps = SanityFooterSettings & {
  logo?: SanityImageFragment
}

const FooterBlock = ({ title, children, className }: FooterBlockProps) => (
  <div
    className={cx(
      'flex flex-col gap-y-5 border-t-[1px] border-pageText lg:pb-[70px]',
      'px-[var(--container-padding)] xs:px-[calc(var(--container-padding)+var(--page-padding))] py-6',
      className,
    )}
  >
    {title && <h4 className="is-h5 font-medium">{title}</h4>}
    {children}
  </div>
)

const FooterLink = ({ id, children, href, icon }: FooterLinkProps) => (
  <a
    href={href}
    className={cx('inline-flex items-start gap-x-2.5 text-base group', {
      'pointer-events-none': !href,
    })}
  >
    {!!icon && (
      <span className="bg-white bg-opacity-[0.05] rounded-full p-2 text-xl">
        <Icon id={id} name={icon} />
      </span>
    )}

    <span className="mt-1 group-hover:opacity-60 transition-opacity duration-200">
      {children}
    </span>
  </a>
)

const Footer = ({
  company,
  menuOne,
  menuTwo,
  legalMenu,
  copyright,
  paymentMethods,
  newsletter,
}: FooterProps) => {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <footer
      className="relative overflow-hidden bg-pageBG text-pageText flex flex-col"
      role="contentinfo"
    >
      <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 border-b-[1px] border-pageText">
        {!!company && (
          <FooterBlock title={company.name} className="xs:border-r-[1px]">
            <div className="flex flex-col">
              {!!company.phone && (
                <FooterLink
                  href={`tel:${company.phone}`}
                  id="footer-company-phone"
                >
                  {company.phone}
                </FooterLink>
              )}
              {!!company.email && (
                <FooterLink
                  href={`mailto:${company.email}`}
                  id="footer-company-email"
                >
                  {company.email}
                </FooterLink>
              )}
              {!!company.address && (
                <FooterLink id="footer-company-address">
                  {company.address}
                </FooterLink>
              )}
            </div>
          </FooterBlock>
        )}

        {!!menuOne && (
          <FooterBlock title={menuOne.title} className="lg:border-r-[1px]">
            <Menu menuType="footer" items={menuOne.items} />
          </FooterBlock>
        )}

        {!!menuTwo && (
          <FooterBlock title={menuTwo.title} className="xs:border-r-[1px]">
            <Menu menuType="footer" items={menuTwo.items} />
          </FooterBlock>
        )}

        {!!newsletter && !!newsletter.settings && isMounted && (
          <FooterBlock title={newsletter.title}>
            <p>{newsletter.description}</p>
            <Newsletter
              id={newsletter.settings._key}
              service="klaviyo"
              klaviyoListID={newsletter.settings.klaviyoListID}
              submit={newsletter.settings.submit}
              successMsg={newsletter.settings.successMsg}
              errorMsg={newsletter.settings.errorMsg}
              terms={newsletter.settings.terms}
            />
          </FooterBlock>
        )}
      </div>

      <div
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 py-5
       px-[calc(var(--container-padding)+var(--page-padding))] text-sm"
      >
        {/* LEFT SIDE: Copyright and Legal */}
        <div className="flex flex-col xs:flex-row xs:items-center xs:flex-wrap gap-3 justify-center">
          {!!copyright && <p>{copyright}</p>}

          {!!legalMenu && (
            <Menu menuType="footer-legal" items={legalMenu.items} />
          )}
        </div>

        {/* RIGHT SIDE: Payment Methods & "Powered by Frameful" */}
        <div className="flex flex-col items-center justify-center gap-6 mt-3 lg:mt-0 lg:flex-row lg:items-center lg:justify-end">
          {/* Payment Methods */}
          {!!paymentMethods && (
            <div className="flex flex-row items-center gap-3">
              {paymentMethods.map((paymentMethod) => (
                <div key={paymentMethod.asset._id}>
                  <Photo
                    image={paymentMethod}
                    width={
                      24 * paymentMethod.asset.metadata.dimensions.aspectRatio
                    }
                    height={24}
                    showPlaceholder={false}
                  />
                </div>
              ))}
            </div>
          )}

          <LanguageSwitch hasCurrency />

          {/* "Powered by Frameful" */}
          <a href="https://frameful.io" className="group">
            <span>Powered by</span> <b>Frameful</b>
          </a>
        </div>
      </div>
    </footer>
  )
}

export default Footer
